import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { paymentsApi, formatApiError } from "./api";
import { ArrowLeft, LogOut, Wallet, CheckCircle2, Users } from "lucide-react";

const FEE_KIND_OPTIONS = [
  ["none", "Sin cargo"],
  ["pct", "% sobre subtotal"],
  ["fixed", "Importe fijo (€)"],
];

const BOOK_MODE_OPTIONS = [
  ["disabled", "Desactivado — sin botón"],
  ["contact_only", "Botón de contacto (email / WhatsApp)"],
  ["accept_form", "Formulario de aceptación del presupuesto"],
  ["custom_url", "Redirigir a URL personalizada"],
  ["deposit", "[Próximamente] Cobrar depósito (Stripe)"],
  ["total", "[Próximamente] Cobrar total (Stripe)"],
  ["stripe_checkout", "[Próximamente] Stripe Checkout"],
];

const PAYMENT_METHODS = [
  ["transfer", "Transferencia bancaria"],
  ["bizum", "Bizum"],
  ["stripe_card", "[Próximamente] Tarjeta (Stripe)"],
  ["stripe_link", "[Próximamente] Link de pago (Stripe)"],
  ["paypal", "[Próximamente] PayPal"],
  ["cash", "Efectivo"],
];

export default function AdminPayments() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [settings, setSettings] = useState(null);
  const [acceptances, setAcceptances] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    paymentsApi.getSettings().then(setSettings).catch((e) => setError(formatApiError(e)));
    paymentsApi.allAcceptances().then(setAcceptances).catch(() => {});
  }, []);

  const update = (patch) => setSettings((s) => ({ ...s, ...patch }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const updated = await paymentsApi.updateSettings({
        currency: settings.currency,
        iva_pct: Number(settings.iva_pct ?? 0),
        prices_include_iva: !!settings.prices_include_iva,
        mgmt_fee_kind: settings.mgmt_fee_kind || "none",
        mgmt_fee_value: Number(settings.mgmt_fee_value ?? 0),
        deposit_kind: settings.deposit_kind || "none",
        deposit_value: Number(settings.deposit_value ?? 0),
        default_book_mode: settings.default_book_mode || "contact_only",
        contact_email: settings.contact_email || "",
        contact_whatsapp: settings.contact_whatsapp || "",
        payment_methods: settings.payment_methods || [],
        accept_form_terms: settings.accept_form_terms || "",
      });
      setSettings(updated);
      setSavedAt(new Date());
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const togglePM = (key) => {
    const current = new Set(settings.payment_methods || []);
    if (current.has(key)) current.delete(key); else current.add(key);
    update({ payment_methods: Array.from(current) });
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="bg-[#0A192F] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/admin")} className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-[#E8C97A]" data-testid="back-dashboard-pay">
            <ArrowLeft size={14} /> Volver
          </button>
          <div className="font-serif leading-tight text-center">
            <div className="text-base">Escapafy</div>
            <div className="italic text-[#E8C97A] text-[0.65rem] tracking-[0.18em] uppercase">Pagos</div>
          </div>
          <div className="flex items-center gap-5">
            <span className="hidden md:inline text-sm text-white/70">{user?.email}</span>
            <button onClick={logout} className="text-white/70 hover:text-[#E8C97A] inline-flex items-center gap-2 text-sm">
              <LogOut size={14} /> Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 lg:px-10 py-10">
        <div className="mb-8">
          <span className="label-eyebrow text-[#16335A]/70">Configuración global</span>
          <h1 className="mt-2 font-display text-3xl lg:text-5xl text-[#0A192F] tracking-tight">
            <span className="italic text-[#16335A]">Pagos</span> y reserva
          </h1>
          <p className="mt-3 text-[#3A506B] max-w-2xl">
            Define la base económica aplicada a todas las propuestas. Cada propuesta puede sobreescribir estos valores individualmente.
          </p>
        </div>

        {error && <p className="text-red-700 bg-red-50 border border-red-200 px-4 py-3 mb-6 text-sm">{error}</p>}

        {!settings && <p className="text-[#3A506B] italic">Cargando...</p>}

        {settings && (
          <form onSubmit={handleSave} className="space-y-8" data-testid="payments-settings-form">
            {/* Moneda + IVA */}
            <Section title="Moneda e impuestos" icon={Wallet}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Moneda">
                  <input value={settings.currency || "EUR"} disabled className="w-full px-4 py-2.5 bg-[#FDFBF7] border border-[#16335A]/15 text-sm text-[#3A506B]" style={{ borderRadius: "2px" }} />
                  <p className="text-[0.65rem] text-[#3A506B] mt-1">Por ahora solo € (multi-moneda en backlog)</p>
                </Field>
                <Field label="IVA (%)">
                  <input type="number" step="0.5" min="0"
                    value={settings.iva_pct ?? 0}
                    onChange={(e) => update({ iva_pct: e.target.value })}
                    data-testid="settings-iva-pct"
                    className="w-full px-4 py-2.5 bg-white border border-[#16335A]/15 text-sm" style={{ borderRadius: "2px" }} />
                </Field>
                <Field label="¿Precios ya incluyen IVA?">
                  <select
                    value={settings.prices_include_iva ? "yes" : "no"}
                    onChange={(e) => update({ prices_include_iva: e.target.value === "yes" })}
                    data-testid="settings-prices-include-iva"
                    className="w-full px-4 py-2.5 bg-white border border-[#16335A]/15 text-sm" style={{ borderRadius: "2px" }}>
                    <option value="yes">Sí, ya incluidos</option>
                    <option value="no">No, se suman al total</option>
                  </select>
                </Field>
              </div>
            </Section>

            {/* Comisión + Depósito */}
            <Section title="Comisión de gestión y depósito" icon={Wallet}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <span className="block text-[0.7rem] font-medium text-[#16335A]/80 uppercase tracking-wider">Comisión de gestión</span>
                  <div className="grid grid-cols-2 gap-3">
                    <select
                      value={settings.mgmt_fee_kind || "none"}
                      onChange={(e) => update({ mgmt_fee_kind: e.target.value })}
                      data-testid="settings-mgmt-fee-kind"
                      className="w-full px-3 py-2 bg-white border border-[#16335A]/15 text-sm" style={{ borderRadius: "2px" }}>
                      {FEE_KIND_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                    <input type="number" step="0.01" min="0"
                      value={settings.mgmt_fee_value ?? 0}
                      onChange={(e) => update({ mgmt_fee_value: e.target.value })}
                      data-testid="settings-mgmt-fee-value"
                      placeholder="0"
                      className="w-full px-3 py-2 bg-white border border-[#16335A]/15 text-sm" style={{ borderRadius: "2px" }} />
                  </div>
                </div>
                <div className="space-y-3">
                  <span className="block text-[0.7rem] font-medium text-[#16335A]/80 uppercase tracking-wider">Depósito predeterminado</span>
                  <div className="grid grid-cols-2 gap-3">
                    <select
                      value={settings.deposit_kind || "none"}
                      onChange={(e) => update({ deposit_kind: e.target.value })}
                      data-testid="settings-deposit-kind"
                      className="w-full px-3 py-2 bg-white border border-[#16335A]/15 text-sm" style={{ borderRadius: "2px" }}>
                      {FEE_KIND_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                    <input type="number" step="0.01" min="0"
                      value={settings.deposit_value ?? 0}
                      onChange={(e) => update({ deposit_value: e.target.value })}
                      data-testid="settings-deposit-value"
                      placeholder="0"
                      className="w-full px-3 py-2 bg-white border border-[#16335A]/15 text-sm" style={{ borderRadius: "2px" }} />
                  </div>
                </div>
              </div>
            </Section>

            {/* Botón Reservar predeterminado */}
            <Section title="Botón “Reservar ahora” — modo por defecto" icon={CheckCircle2}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Modo predeterminado">
                  <select
                    value={settings.default_book_mode || "contact_only"}
                    onChange={(e) => update({ default_book_mode: e.target.value })}
                    data-testid="settings-default-book-mode"
                    className="w-full px-4 py-2.5 bg-white border border-[#16335A]/15 text-sm" style={{ borderRadius: "2px" }}>
                    {BOOK_MODE_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </Field>
                <Field label="Email de contacto">
                  <input type="email" value={settings.contact_email || ""}
                    onChange={(e) => update({ contact_email: e.target.value })}
                    data-testid="settings-contact-email"
                    placeholder="hola@escapafy.com"
                    className="w-full px-4 py-2.5 bg-white border border-[#16335A]/15 text-sm" style={{ borderRadius: "2px" }} />
                </Field>
                <Field label="WhatsApp (E.164)">
                  <input type="text" value={settings.contact_whatsapp || ""}
                    onChange={(e) => update({ contact_whatsapp: e.target.value })}
                    data-testid="settings-contact-whatsapp"
                    placeholder="+34644088078"
                    className="w-full px-4 py-2.5 bg-white border border-[#16335A]/15 text-sm" style={{ borderRadius: "2px" }} />
                </Field>
                <Field label="Términos del formulario de aceptación">
                  <textarea value={settings.accept_form_terms || ""}
                    onChange={(e) => update({ accept_form_terms: e.target.value })}
                    rows={3}
                    placeholder="Texto legal mostrado bajo el checkbox de aceptación..."
                    data-testid="settings-accept-form-terms"
                    className="w-full px-4 py-2 bg-white border border-[#16335A]/15 text-sm" style={{ borderRadius: "2px" }} />
                </Field>
              </div>
            </Section>

            {/* Métodos de pago */}
            <Section title="Métodos de pago disponibles" icon={Wallet}>
              <p className="text-sm text-[#3A506B] mb-4 italic">
                Marca los métodos que aceptas. Por ahora son informativos; las pasarelas activas se conectarán más adelante.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {PAYMENT_METHODS.map(([k, l]) => {
                  const checked = (settings.payment_methods || []).includes(k);
                  return (
                    <label
                      key={k}
                      className={`flex items-center gap-3 px-4 py-3 border cursor-pointer transition-colors ${
                        checked ? "border-[#16335A] bg-[#FDFBF7]" : "border-[#16335A]/15 hover:border-[#16335A]/40"
                      }`}
                      style={{ borderRadius: "2px" }}
                    >
                      <input type="checkbox" checked={checked} onChange={() => togglePM(k)} data-testid={`settings-pm-${k}`} className="accent-[#16335A]" />
                      <span className="text-sm text-[#0A192F]">{l}</span>
                    </label>
                  );
                })}
              </div>
            </Section>

            {/* Save bar */}
            <div className="sticky bottom-4 bg-white border border-[#16335A]/15 px-5 py-4 flex items-center justify-between" style={{ borderRadius: "2px" }}>
              <div className="text-sm text-[#3A506B] italic">
                {savedAt ? `Guardado · ${savedAt.toLocaleTimeString("es-ES").slice(0, 5)}` : "Cambios sin guardar"}
              </div>
              <button type="submit"
                disabled={saving}
                data-testid="settings-save-btn"
                className="bg-[#16335A] hover:bg-[#214A82] disabled:opacity-60 text-white px-6 py-3 rounded-full text-sm font-semibold inline-flex items-center gap-2">
                {saving ? "Guardando..." : "Guardar cambios"}
              </button>
            </div>
          </form>
        )}

        {/* Acceptances */}
        {acceptances.length > 0 && (
          <section className="mt-12 bg-white border border-[#16335A]/15 p-6 lg:p-8" style={{ borderRadius: "2px" }}>
            <h2 className="font-display text-2xl text-[#0A192F] mb-4 inline-flex items-center gap-2">
              <Users size={18} /> Aceptaciones de presupuesto ({acceptances.length})
            </h2>
            <div className="space-y-3">
              {acceptances.map((a) => (
                <div key={a.id} className="bg-[#FDFBF7] border border-[#16335A]/10 p-4 text-sm" style={{ borderRadius: "2px" }}>
                  <div className="flex flex-wrap items-baseline gap-x-4">
                    <strong className="text-[#0A192F]">{a.client_name}</strong>
                    <span className="text-[#3A506B]">{a.client_email}</span>
                    {a.client_phone && <span className="text-[#3A506B]">{a.client_phone}</span>}
                    <span className="text-xs text-[#3A506B]/70 ml-auto">
                      {new Date(a.accepted_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })}
                    </span>
                  </div>
                  <a href={`/p/${a.proposal_token}`} target="_blank" rel="noreferrer" className="text-xs text-[#16335A] hover:underline mt-1 inline-block">
                    Ver propuesta →
                  </a>
                  {a.notes && <p className="text-[#3A506B] italic mt-2">“{a.notes}”</p>}
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <section className="bg-white border border-[#16335A]/15 p-6 lg:p-8" style={{ borderRadius: "2px" }}>
      <div className="flex items-center gap-3 mb-5">
        {Icon && <Icon size={16} className="text-[#16335A]" />}
        <h2 className="font-display text-xl text-[#0A192F]">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[0.7rem] font-medium text-[#16335A]/80 mb-1.5 uppercase tracking-wider">{label}</span>
      {children}
    </label>
  );
}
